import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as DM from '@radix-ui/react-dropdown-menu';
import {
  ArrowDown,
  ArrowUp,
  Check,
  CircleAlert,
  Crown,
  ListVideo,
  Lock,
  LogOut,
  MessageCircle,
  MoreHorizontal,
  Play,
  Plus,
  Search,
  SendHorizontal,
  SkipForward,
  ThumbsUp,
  UserX,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  PARTY_CHAT_MAX,
  PARTY_REACTIONS,
  partyMediaLabel,
  partyPlayableText,
  type MediaSummary,
  type PartyMember,
  type PartyReaction,
  type PartyState,
} from '@teaplus/shared';
import { ProfileAvatar } from '../profile/avatar';
import { Switch } from '../ui/misc';
import { api, errorMessage } from '@/lib/api';
import { useParty, type PartyConnection } from '@/lib/party';
import { useActiveProfile } from '@/lib/queries';
import { cn } from '@/lib/cn';

type Tab = 'chat' | 'next' | 'people';

const fail = (e: unknown) => toast.error(errorMessage(e));

/** The chat, queue and people over the player. */
export function PartyPanel({ code, onClose }: { code: string; onClose: () => void }) {
  const { conn, state, messages, isHost, me } = useParty(code);
  const [tab, setTab] = useState<Tab>('chat');
  useEffect(() => {
    if (!conn) return;
    conn.setChatOpen(tab === 'chat');
    return () => conn.setChatOpen(false);
  }, [conn, tab]);
  if (!conn || !state) return null;

  return (
    <aside
      aria-label="Party panel"
      className="absolute bottom-24 right-4 top-20 z-[45] flex w-[min(22rem,calc(100%-2rem))] animate-[rise_220ms_var(--ease-out-soft)] flex-col overflow-hidden rounded-2xl border border-white/10 bg-bg-2/95 shadow-2xl backdrop-blur-xl sm:right-6"
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-1 border-b border-line p-2">
        <TabButton active={tab === 'chat'} onClick={() => setTab('chat')} icon={<MessageCircle />}>
          Chat
        </TabButton>
        <TabButton active={tab === 'next'} onClick={() => setTab('next')} icon={<ListVideo />}>
          Queue
          {state.poll.length > 0 && <Count>{state.poll.length}</Count>}
        </TabButton>
        <TabButton active={tab === 'people'} onClick={() => setTab('people')} icon={<Users />}>
          People <Count>{state.members.length}</Count>
        </TabButton>
        <button
          onClick={onClose}
          aria-label="Close Party panel"
          className="ml-auto grid h-8 w-8 place-items-center rounded-full text-muted hover:bg-white/8 hover:text-text"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1">
        {tab === 'chat' && <Chat conn={conn} messages={messages} meId={me?.id ?? null} />}
        {tab === 'next' && (
          <PartyQueue conn={conn} state={state} isHost={isHost} meId={me?.id ?? null} />
        )}
        {tab === 'people' && <People conn={conn} state={state} isHost={isHost} />}
      </div>
    </aside>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors [&_svg]:h-3.5 [&_svg]:w-3.5',
        active ? 'bg-white/10 text-text' : 'text-muted hover:text-text',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function Count({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-full bg-white/10 px-1.5 text-[10px] tabular-nums">{children}</span>
  );
}

export function Chat({
  conn,
  messages,
  meId,
}: {
  conn: PartyConnection;
  messages: ReturnType<typeof useParty>['messages'];
  meId: string | null;
}) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const list = useRef<HTMLDivElement>(null);
  const kids = useActiveProfile().data?.profile.isKids;

  useEffect(() => {
    list.current?.scrollTo({ top: list.current.scrollHeight });
  }, [messages.length]);

  async function send(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await conn.act({ type: 'chat', text });
      setText('');
    } catch (err) {
      fail(err);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div ref={list} className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3" aria-live="polite">
        {messages.length === 0 && (
          <p className="pt-6 text-center text-sm text-muted">Say hello to everyone.</p>
        )}
        {messages.map((m) =>
          m.kind === 'system' ? (
            <p key={m.id} className="text-center text-xs text-muted">
              {m.text}
            </p>
          ) : (
            <div key={m.id} className={cn('flex flex-col', m.memberId === meId && 'items-end')}>
              <span className="mb-0.5 px-1 text-[11px] font-semibold text-muted">
                {m.memberId === meId ? 'You' : m.name}
              </span>
              <p
                className={cn(
                  'max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3 py-1.5 text-sm',
                  m.memberId === meId
                    ? 'rounded-br-md bg-pink text-on-pink'
                    : 'rounded-bl-md bg-white/8',
                )}
              >
                {m.text}
              </p>
            </div>
          ),
        )}
      </div>
      <div
        className="flex justify-between gap-0.5 border-t border-line px-2 pt-2"
        role="group"
        aria-label="Reactions"
      >
        {PARTY_REACTIONS.map((emoji) => (
          <button
            key={emoji}
            onClick={() => void conn.act({ type: 'react', emoji }).catch(fail)}
            aria-label={`React ${emoji}`}
            className="grid h-9 w-9 place-items-center rounded-full text-xl transition-transform hover:scale-125 hover:bg-white/8 active:scale-95"
          >
            {emoji}
          </button>
        ))}
      </div>
      {kids ? (
        <p className="p-3 text-center text-xs text-muted">Kids profiles can send reactions.</p>
      ) : (
        <form onSubmit={send} className="flex gap-2 p-2">
          <input
            value={text}
            maxLength={PARTY_CHAT_MAX}
            onChange={(e) => setText(e.target.value)}
            placeholder="Send a message"
            aria-label="Chat message"
            className="h-10 min-w-0 flex-1 rounded-xl border border-line bg-bg px-3 text-sm outline-none focus:border-pink/70"
          />
          <button
            type="submit"
            disabled={!text.trim() || sending}
            aria-label="Send message"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-pink text-on-pink disabled:opacity-40"
          >
            <SendHorizontal className="h-4 w-4" />
          </button>
        </form>
      )}
    </div>
  );
}

/** Finds a title and adds it to the queue. Floating, its results open over the page. */
export function AddTitleSearch({
  conn,
  state,
  floating,
  large,
  autoFocus,
}: {
  conn: PartyConnection;
  state: PartyState;
  floating?: boolean;
  large?: boolean;
  autoFocus?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 300);
    return () => window.clearTimeout(t);
  }, [query]);
  const results = useQuery({
    queryKey: ['party-suggest', debounced],
    queryFn: () =>
      api<{ titles: MediaSummary[] }>(
        `/api/content/search/suggest?q=${encodeURIComponent(debounced)}`,
      ),
    enabled: debounced.length > 1,
  });
  // The next episode of what's on, or of what just finished.
  const m = state.hasTitle ? state.media : state.lastPlayed;
  const nominate = (media: Parameters<PartyConnection['nominate']>[0]) =>
    conn
      .nominate(media)
      .then(() => setQuery(''))
      .catch(fail);

  return (
    <div className={cn('space-y-2', floating && 'relative')}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
        <input
          value={query}
          autoFocus={autoFocus}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Add a film or show"
          aria-label="Add a title to the queue"
          className={cn(
            'w-full rounded-xl border border-line bg-bg pl-9 pr-3 outline-none focus:border-pink/70',
            large ? 'h-13 text-base' : 'h-10 text-sm',
          )}
        />
      </div>
      {m?.type === 'series' && m.season != null && !query && (
        <button
          onClick={() =>
            void nominate({
              type: 'series',
              id: m.id,
              season: m.season,
              episode: (m.episode ?? 0) + 1,
            })
          }
          className="flex w-full items-center gap-2 rounded-xl bg-white/6 px-3 py-2 text-left text-sm font-medium hover:bg-white/10"
        >
          <SkipForward className="h-4 w-4 text-pink" /> Add the next episode, S{m.season} E
          {(m.episode ?? 0) + 1}
        </button>
      )}
      {debounced.length > 1 && query && (
        <ul
          className={cn(
            'space-y-1 overflow-y-auto',
            floating
              ? 'absolute inset-x-0 top-full z-40 mt-1 max-h-72 rounded-xl border border-line bg-bg-2 p-2 shadow-2xl'
              : 'max-h-48',
          )}
        >
          {(results.data?.titles ?? []).map((t) => (
            <li key={t.id}>
              <button
                onClick={() =>
                  void nominate({
                    type: t.type,
                    id: t.id,
                    season: t.type === 'series' ? 1 : null,
                    episode: t.type === 'series' ? 1 : null,
                  })
                }
                className="flex w-full items-center gap-3 rounded-xl p-1.5 text-left hover:bg-white/6"
              >
                {t.posterUrl ? (
                  <img
                    loading="lazy"
                    decoding="async"
                    src={t.posterUrl}
                    alt=""
                    className="h-12 w-8 rounded object-cover"
                  />
                ) : (
                  <span className="h-12 w-8 rounded bg-card" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{t.title}</span>
                  <span className="text-xs text-muted">
                    {[t.type === 'series' ? 'Series, from S1 E1' : 'Film', t.year]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <Plus className="h-4 w-4 text-pink" />
              </button>
            </li>
          ))}
          {results.data?.titles.length === 0 && (
            <li className="px-2 text-xs text-muted">Nothing found.</li>
          )}
        </ul>
      )}
    </div>
  );
}

export function PartyQueue({
  conn,
  state,
  isHost,
  meId,
}: {
  conn: PartyConnection;
  state: PartyState;
  isHost: boolean;
  meId: string | null;
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-line p-3">
        <AddTitleSearch conn={conn} state={state} />
      </div>
      <ul className="scrollbar-thin min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
        {state.poll.length === 0 && (
          <li className="px-4 pt-6 text-center text-sm leading-relaxed text-muted">
            Nothing queued yet. Find something here, or browse and use Add to queue on its page.
          </li>
        )}
        {state.poll.map((o, i) => {
          const voted = !!meId && o.votes.includes(meId);
          const label = partyMediaLabel(o.media);
          const cant = state.members.filter((x) => o.playable[x.id] === false);
          return (
            <li key={o.id} className="group flex gap-3 rounded-xl p-2 hover:bg-white/5">
              {o.media.posterUrl ? (
                <img
                  loading="lazy"
                  decoding="async"
                  src={o.media.posterUrl}
                  alt=""
                  className="h-[72px] w-12 shrink-0 rounded-lg object-cover"
                />
              ) : (
                <span className="h-[72px] w-12 shrink-0 rounded-lg bg-card" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{label}</p>
                <p className="truncate text-xs text-muted">Added by {o.by}</p>
                <p
                  className={cn(
                    'mt-0.5 flex items-center gap-1 truncate text-xs',
                    cant.length ? 'text-warning' : 'text-muted',
                  )}
                  title={
                    cant.length
                      ? `Can't play this: ${cant.map((x) => x.name).join(', ')}`
                      : undefined
                  }
                >
                  {cant.length > 0 && <CircleAlert className="h-3 w-3 shrink-0" />}
                  {partyPlayableText(o, state.members)}
                </p>
                <div className="mt-1.5 flex items-center gap-0.5">
                  <button
                    onClick={() =>
                      void conn.act({ type: 'vote', optionId: voted ? null : o.id }).catch(fail)
                    }
                    aria-pressed={voted}
                    aria-label={`${voted ? 'Remove vote for' : 'Vote for'} ${label}`}
                    className={cn(
                      'mr-1 flex h-7 items-center gap-1 rounded-full px-2 text-xs font-semibold tabular-nums',
                      voted ? 'bg-pink/20 text-pink' : 'bg-white/8 hover:bg-white/12',
                    )}
                  >
                    <ThumbsUp className="h-3 w-3" /> {o.votes.length}
                  </button>
                  {isHost && (
                    <>
                      <RowAction
                        label={`Start ${label} now`}
                        onClick={() => conn.act({ type: 'startNow', optionId: o.id })}
                        primary
                      >
                        <Play className="h-3.5 w-3.5 fill-current" />
                      </RowAction>
                      <RowAction
                        label={`Move ${label} up`}
                        disabled={i === 0}
                        onClick={() => conn.act({ type: 'reorder', optionId: o.id, index: i - 1 })}
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </RowAction>
                      <RowAction
                        label={`Move ${label} down`}
                        disabled={i === state.poll.length - 1}
                        onClick={() => conn.act({ type: 'reorder', optionId: o.id, index: i + 1 })}
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </RowAction>
                      <RowAction
                        label={`Remove ${label}`}
                        onClick={() => conn.act({ type: 'unnominate', optionId: o.id })}
                      >
                        <X className="h-3.5 w-3.5" />
                      </RowAction>
                    </>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RowAction({
  label,
  onClick,
  disabled,
  primary,
  children,
}: {
  label: string;
  onClick: () => Promise<unknown>;
  disabled?: boolean;
  primary?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      disabled={disabled}
      onClick={() => void onClick().catch(fail)}
      aria-label={label}
      title={label}
      className={cn(
        'grid h-7 w-7 place-items-center rounded-full transition-colors disabled:opacity-25',
        primary
          ? 'bg-pink text-on-pink hover:bg-pink-hover'
          : 'text-muted hover:bg-white/10 hover:text-text',
      )}
    >
      {children}
    </button>
  );
}

function People({
  conn,
  state,
  isHost,
}: {
  conn: PartyConnection;
  state: PartyState;
  isHost: boolean;
}) {
  const rc = state.readyCheck;
  return (
    <div className="h-full space-y-4 overflow-y-auto p-3">
      <ul className="space-y-1">
        {state.members.map((m) => (
          <li key={m.id} className="flex items-center gap-2.5 rounded-xl p-1.5">
            <ProfileAvatar
              avatar={m.avatar}
              image={m.avatarUrl}
              size={32}
              rounded="full"
              className={cn(!m.connected && 'opacity-40')}
            />
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1.5 truncate text-sm font-medium">
                {m.id === state.you ? 'You' : m.name}
                {m.isHost && <Crown className="h-3.5 w-3.5 text-pink" aria-label="Owner" />}
              </span>
              <span className="block text-[11px] text-muted">{statusOf(m, rc)}</span>
            </span>
            {isHost && m.id !== state.you && <MemberMenu conn={conn} member={m} />}
          </li>
        ))}
      </ul>
      {isHost && (
        <div className="space-y-3 rounded-xl bg-white/4 p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted">
            Owner controls
          </p>
          <label className="flex items-center gap-3 text-sm">
            <span className="flex-1">Only I can play, pause and skip</span>
            <Switch
              label="Only the owner controls playback"
              checked={state.settings.hostOnlyControls}
              onCheckedChange={(on) =>
                void conn.act({ type: 'settings', hostOnlyControls: on }).catch(fail)
              }
            />
          </label>
          <label className="flex items-center gap-3 text-sm">
            <span className="flex flex-1 items-center gap-1.5">
              <Lock className="h-3.5 w-3.5" /> Lock the party
            </span>
            <Switch
              label="Lock the party"
              checked={state.settings.locked}
              onCheckedChange={(on) => void conn.act({ type: 'settings', locked: on }).catch(fail)}
            />
          </label>
          <button
            onClick={() => void conn.act({ type: 'readyCheck' }).catch(fail)}
            disabled={!!rc && !rc.done}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-white/8 py-2 text-sm font-semibold hover:bg-white/12 disabled:opacity-50"
          >
            <Check className="h-4 w-4" />{' '}
            {rc && !rc.done ? 'Ready check running...' : 'Ready check'}
          </button>
          <button
            onClick={() => void conn.act({ type: 'end' }).catch(fail)}
            className="flex w-full items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold text-danger hover:bg-white/8"
          >
            <LogOut className="h-4 w-4" /> End Party
          </button>
        </div>
      )}
    </div>
  );
}

export function statusOf(m: PartyMember, rc: PartyState['readyCheck']) {
  if (rc && m.id in rc.answers) return rc.answers[m.id] ? 'Ready' : 'Not ready yet';
  if (!m.connected) return 'Away';
  if (m.buffering) return 'Buffering';
  if (m.ready) return 'Watching';
  return partyActivityText(m);
}

/** "Looking at Sintel", "Browsing Movies". */
export function partyActivityText(m: PartyMember) {
  if (m.sittingOut && !m.activity) return 'In the menus';
  if (m.activity?.media) return `Looking at ${m.activity.media.title}`;
  return m.activity?.place || 'Joined';
}

const menuItem =
  'flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm outline-none data-[highlighted]:bg-white/8';

/** The owner's settings in the panel's ··· menu, and leaving without ending the party. */
export function PartyMenu({
  conn,
  state,
  onLeave,
}: {
  conn: PartyConnection;
  state: PartyState;
  onLeave: () => void;
}) {
  const rc = state.readyCheck;
  return (
    <DM.Root modal={false}>
      <DM.Trigger
        aria-label="Party options"
        className="grid h-8 w-8 place-items-center rounded-full text-muted outline-none hover:bg-white/8 hover:text-text"
      >
        <MoreHorizontal className="h-4 w-4" />
      </DM.Trigger>
      <DM.Portal>
        <DM.Content
          align="end"
          sideOffset={6}
          className="z-[80] min-w-64 rounded-xl border border-line bg-bg-2 p-1 shadow-2xl"
        >
          <DM.Item
            disabled={!!rc && !rc.done}
            onSelect={() => void conn.act({ type: 'readyCheck' }).catch(fail)}
            className={cn(menuItem, 'data-[disabled]:opacity-50')}
          >
            <Check className="h-4 w-4" />
            {rc && !rc.done ? 'Ready check running...' : 'Ready check'}
          </DM.Item>
          <DM.CheckboxItem
            checked={state.settings.locked}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(on) => void conn.act({ type: 'settings', locked: on }).catch(fail)}
            className={menuItem}
          >
            <Lock className="h-4 w-4" />
            <span className="flex-1">Lock the party</span>
            <DM.ItemIndicator>
              <Check className="h-4 w-4 text-pink" />
            </DM.ItemIndicator>
          </DM.CheckboxItem>
          <DM.CheckboxItem
            checked={state.settings.hostOnlyControls}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(on) =>
              void conn.act({ type: 'settings', hostOnlyControls: on }).catch(fail)
            }
            className={menuItem}
          >
            <Crown className="h-4 w-4" />
            <span className="flex-1">Only I can play, pause and skip</span>
            <DM.ItemIndicator>
              <Check className="h-4 w-4 text-pink" />
            </DM.ItemIndicator>
          </DM.CheckboxItem>
          <DM.Separator className="my-1 h-px bg-line" />
          <DM.Item onSelect={onLeave} className={menuItem}>
            <LogOut className="h-4 w-4" /> Leave Party
          </DM.Item>
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}

export function MemberMenu({
  conn,
  member,
  children,
}: {
  conn: PartyConnection;
  member: PartyMember;
  children?: ReactNode;
}) {
  return (
    <DM.Root modal={false}>
      <DM.Trigger
        aria-label={`Options for ${member.name}`}
        className={
          children
            ? 'rounded-full outline-none focus-visible:ring-2 focus-visible:ring-pink'
            : 'grid h-8 w-8 place-items-center rounded-full text-muted outline-none hover:bg-white/8 hover:text-text'
        }
      >
        {children ?? <MoreHorizontal className="h-4 w-4" />}
      </DM.Trigger>
      <DM.Portal container={document.fullscreenElement ?? undefined}>
        <DM.Content
          align="end"
          className="z-[80] min-w-44 rounded-xl border border-line bg-bg-2 p-1 shadow-2xl"
        >
          <DM.Item
            onSelect={() => void conn.act({ type: 'makeHost', memberId: member.id }).catch(fail)}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm outline-none data-[highlighted]:bg-white/8"
          >
            <Crown className="h-4 w-4" /> Make owner
          </DM.Item>
          <DM.Item
            onSelect={() => void conn.act({ type: 'kick', memberId: member.id }).catch(fail)}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm text-danger outline-none data-[highlighted]:bg-white/8"
          >
            <UserX className="h-4 w-4" /> Remove from party
          </DM.Item>
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}

export function ReadyCheckCard({ code }: { code: string }) {
  const { conn, state, me } = useParty(code);
  const rc = state?.readyCheck;
  if (!conn || !rc || rc.done || !me || me.id in rc.answers) return null;
  return (
    <div
      role="alertdialog"
      aria-label="Ready check"
      className="absolute left-1/2 top-20 z-50 w-[min(22rem,calc(100%-2rem))] -translate-x-1/2 animate-[rise_220ms_var(--ease-out-soft)] rounded-2xl border border-white/10 bg-bg-2/95 p-4 text-center shadow-2xl backdrop-blur-xl"
    >
      <p className="font-semibold">{rc.by} is checking everyone is ready</p>
      <div className="mt-3 flex justify-center gap-2">
        <button
          autoFocus
          onClick={() => void conn.act({ type: 'readyAnswer', ready: true }).catch(fail)}
          className="flex items-center gap-1.5 rounded-xl bg-pink px-4 py-2 text-sm font-semibold text-on-pink"
        >
          <Check className="h-4 w-4" /> I&rsquo;m ready
        </button>
        <button
          onClick={() => void conn.act({ type: 'readyAnswer', ready: false }).catch(fail)}
          className="rounded-xl bg-white/10 px-4 py-2 text-sm font-semibold"
        >
          Not yet
        </button>
      </div>
    </div>
  );
}

export function Reactions({ code }: { code: string }) {
  const { conn } = useParty(code);
  const [items, setItems] = useState<(PartyReaction & { x: number })[]>([]);
  useEffect(() => {
    if (!conn) return;
    return conn.onReaction((r) => {
      const item = { ...r, x: 8 + Math.random() * 18 };
      setItems((list) => [...list.slice(-20), item]);
      window.setTimeout(() => setItems((list) => list.filter((i) => i.id !== r.id)), 3200);
    });
  }, [conn]);
  return (
    <div className="pointer-events-none absolute inset-0 z-30 overflow-hidden" aria-hidden>
      {items.map((r) => (
        <div
          key={r.id}
          className="absolute bottom-28 flex flex-col items-center animate-[float-up_3s_ease-out_forwards]"
          style={{ right: `${r.x}%` }}
        >
          <span className="text-4xl drop-shadow-lg">{r.emoji}</span>
          <span className="mt-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-semibold">
            {r.name}
          </span>
        </div>
      ))}
    </div>
  );
}
